import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Home, Guitar, Radio, ListMusic, BarChart2, Palette, Shield, HelpCircle } from 'lucide-react';
import UserMenu from './UserMenu';
import { useTheme } from '../context/ThemeContext';
import { useAdmin } from '../context/AdminContext';
import { useCanEdit } from '../context/BandContext';

const MAIN_TABS = [
  { label: 'Home',  Icon: Home,      href: '/dashboard'          },
  { label: 'Gigs',  Icon: Guitar,    href: '/dashboard/gigs'     },
  { label: 'Live',  Icon: Radio,     href: '/dashboard/requests' },
  { label: 'Songs', Icon: ListMusic, href: '/dashboard/songs'    },
];

const UTILITY_TABS = [
  { label: 'Analytics', Icon: BarChart2,  href: '/dashboard/analytics' },
  { label: 'Branding',  Icon: Palette,    href: '/dashboard/branding'  },
  { label: 'Help',      Icon: HelpCircle, href: '/dashboard/help'      },
];

function NavItem({ label, Icon, href, active, theme, navigate }) {
  return (
    <button
      onClick={() => navigate(href)}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 16px',
        background: active ? `${theme.primary}18` : 'none',
        border: 'none',
        borderLeft: `3px solid ${active ? theme.primary : 'transparent'}`,
        cursor: 'pointer',
        color: active ? theme.primary : theme.textMuted,
        fontSize: 14,
        fontWeight: active ? 600 : 400,
        textAlign: 'left',
        WebkitTapHighlightColor: 'transparent',
        transition: 'background 0.15s, color 0.15s',
        boxSizing: 'border-box',
      }}
    >
      <Icon size={18} strokeWidth={active ? 2.5 : 1.8} />
      {label}
    </button>
  );
}

export default function SidebarNav({ bandName, logoUrl }) {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin } = useAdmin();
  const canEdit = useCanEdit();

  const activeTab =
    location.pathname.startsWith('/dashboard/requests') ? 'Live'      :
    location.pathname.startsWith('/dashboard/songs')    ? 'Songs'     :
    location.pathname.startsWith('/dashboard/gigs')     ? 'Gigs'      :
    location.pathname.startsWith('/dashboard/analytics')? 'Analytics' :
    location.pathname.startsWith('/dashboard/branding') ? 'Branding'  :
    location.pathname.startsWith('/dashboard/help')     ? 'Help'      :
    location.pathname.startsWith('/admin')              ? 'Admin'     :
    'Home';

  return (
    <div style={{
      width: 160,
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      background: theme.surface,
      borderRight: `1px solid ${theme.border}`,
      height: '100%',
      overflow: 'hidden',
    }}>
      <div style={{
        background: theme.navGradient,
        padding: '20px 16px 18px',
        flexShrink: 0,
      }}>
        <div style={{
          fontSize: 10,
          fontFamily: 'monospace',
          color: 'rgba(255,255,255,0.65)',
          letterSpacing: '0.08em',
          marginBottom: 8,
        }}>
          GIGREQUEST
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {logoUrl && (
            <img
              src={logoUrl}
              alt="Band logo"
              style={{
                width: 36,
                height: 36,
                objectFit: 'contain',
                borderRadius: 6,
                background: 'rgba(255,255,255,0.15)',
                flexShrink: 0,
              }}
            />
          )}
          {bandName && (
            <div style={{
              fontSize: 14,
              fontWeight: 700,
              color: '#fff',
              lineHeight: 1.3,
              wordBreak: 'break-word',
            }}>
              {bandName}
            </div>
          )}
        </div>
      </div>

      <div style={{ flex: 1, paddingTop: 8, display: 'flex', flexDirection: 'column' }}>
        {MAIN_TABS.map(({ label, Icon, href }) => (
          <NavItem key={label} label={label} Icon={Icon} href={href} active={activeTab === label} theme={theme} navigate={navigate} />
        ))}

        <div style={{ margin: '8px 16px', borderTop: `1px solid ${theme.border}` }} />

        {UTILITY_TABS.filter(t => canEdit || t.label !== 'Branding').map(({ label, Icon, href }) => (
          <NavItem key={label} label={label} Icon={Icon} href={href} active={activeTab === label} theme={theme} navigate={navigate} />
        ))}

        {isAdmin && (
          <>
            <div style={{ margin: '8px 16px', borderTop: `1px solid ${theme.border}` }} />
            <NavItem label="Admin" Icon={Shield} href="/admin" active={activeTab === 'Admin'} theme={theme} navigate={navigate} />
          </>
        )}
      </div>

      <div style={{
        padding: '12px 16px',
        borderTop: `1px solid ${theme.border}`,
        flexShrink: 0,
      }}>
        <UserMenu />
      </div>
    </div>
  );
}
